import { useDispatch, useSelector } from "react-redux";
import { fetchEmployees } from "../store/slices/employeeSlice";
import { useEffect, useMemo } from "react";

// Currency mapping
const currencyMapping = {
  "+1": "$",
  "1": "$",
  "+44": "£",
  "44": "£",
  "+61": "$",
  "61": "$",
  "+91": "₹",
  "91": "₹",
  "+81": "¥",
  "81": "¥",
  "+971": "د.إ",
  "971": "د.إ",
};

const regionCurrencyMapping = {
  USA: "$",
  US: "$",
  INDIA: "₹",
  IN: "₹",
  UK: "£",
  UAE: "د.إ",
  JAPAN: "¥",
};

const regionCountryMapping = {
  USA: "us",
  US: "us",
  INDIA: "in",
  IN: "in",
  UK: "gb",
  UAE: "ae",
  JAPAN: "jp",
};

const countryCodeToIsoMapping = {
  "+1": "us",
  "1": "us",
  "+44": "gb",
  "44": "gb",
  "+91": "in",
  "91": "in",
  "+61": "au",
  "61": "au",
  "+81": "jp",
  "81": "jp",
  "+971": "ae",
  "971": "ae",
};

const normalizeCountryCode = (code) => String(code || "").trim().replace(/\s+/g, "");
const normalizeEmail = (email) => String(email || "").trim().toLowerCase();
const normalizeRegion = (region) => String(region || "").trim().toUpperCase();

const findL0AdminEmployee = (employees = []) => {
  if (!Array.isArray(employees)) return null;
  return (
    employees.find(
      (emp) =>
        String(emp?.EmpAuthLevel || "").trim().toUpperCase() === "L0" &&
        (normalizeCountryCode(emp?.countrycode) || normalizeRegion(emp?.region))
    ) || null
  );
};

export const resolveLoggedUserLocale = (employees = []) => {
  const email = normalizeEmail(localStorage.getItem("email"));
  const fromEmployees = email
    ? employees?.find((emp) => normalizeEmail(emp?.EmpEmail) === email)
    : null;

  const sessionCountryCode = normalizeCountryCode(localStorage.getItem("countrycode"));
  const sessionRegion = normalizeRegion(localStorage.getItem("region"));
  const employeeCountryCode = normalizeCountryCode(fromEmployees?.countrycode);
  const employeeRegion = normalizeRegion(fromEmployees?.region);

  let resolvedCountryCode = employeeCountryCode || sessionCountryCode || "";
  let resolvedRegion = employeeRegion || sessionRegion || "";

  // Fallback for users without their own locale (e.g. Support users):
  // use the L0 (admin) user's countrycode/region from the employees list.
  if (!resolvedCountryCode && !resolvedRegion) {
    const l0Admin = findL0AdminEmployee(employees);
    if (l0Admin) {
      resolvedCountryCode = normalizeCountryCode(l0Admin.countrycode);
      resolvedRegion = normalizeRegion(l0Admin.region);
    }
  }

  return {
    countrycode: resolvedCountryCode,
    region: resolvedRegion,
    employee: fromEmployees || null,
  };
};

export const resolveCurrencySymbol = ({ countrycode, region } = {}) => {
  const normalizedCode = normalizeCountryCode(countrycode);
  if (currencyMapping[normalizedCode]) return currencyMapping[normalizedCode];
  const regionKey = normalizeRegion(region);
  return regionCurrencyMapping[regionKey] || "";
};

export const resolveCountryIso = ({ countrycode, region } = {}) => {
  const normalizedCode = normalizeCountryCode(countrycode);
  if (countryCodeToIsoMapping[normalizedCode]) {
    return countryCodeToIsoMapping[normalizedCode];
  }
  const regionKey = normalizeRegion(region);
  return regionCountryMapping[regionKey] || "";
};

export const resolveCountryInputConfig = ({ countrycode, region } = {}) => {
  const iso2 = resolveCountryIso({ countrycode, region });
  return {
    countrycode: normalizeCountryCode(countrycode),
    iso2,
    onlyCountries: iso2 ? [iso2] : undefined,
    currency: resolveCurrencySymbol({ countrycode, region }),
  };
};

export const useCurrencySymbol = () => {
  const dispatch = useDispatch();
  const { employees } = useSelector((state) => state.employees);

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  return useMemo(() => {
    const locale = resolveLoggedUserLocale(employees);
    return resolveCurrencySymbol(locale);
  }, [employees]);
};
